import ReferencePicker from "@/components/shared/pickers/reference-picker";
import type { StepType } from "@/types/workflow.types";
import { HighlightedCodeEditor } from "./highlighted-code-editor";
import { configEditorLanguage } from "./structured-config-utils";
import type { CatalogFieldDef } from "./catalog-step-config-utils";
import { FieldShell, UuidListField } from "./catalog-step-domain-primitives";
import { getReferenceFieldConfig } from "./reference-field-registry";

type DomainFieldKind = "reference" | "uuid-list" | "markdown";

interface DomainFieldProps {
  kind: DomainFieldKind;
  field: CatalogFieldDef;
  nodeType: StepType;
  value: unknown;
  draft: string;
  error?: string;
  setValue: (key: string, value: unknown) => void;
  setDraft: (key: string, value: string) => void;
}

export function catalogStepDomainFieldKind(
  field: CatalogFieldDef,
  nodeType: StepType,
): DomainFieldKind | null {
  const key = field.key.toLowerCase();
  if (getReferenceFieldConfig(field.key, nodeType)) return "reference";
  if (key.endsWith("_ids")) return "uuid-list";
  if (key.includes("prompt") || key.includes("template") || key === "message") return "markdown";
  return null;
}

export function CatalogStepDomainField({
  kind,
  field,
  nodeType,
  value,
  draft,
  error,
  setValue,
  setDraft,
}: DomainFieldProps) {
  switch (kind) {
    case "reference":
      return <ReferenceField field={field} nodeType={nodeType} value={value} setValue={setValue} />;
    case "uuid-list":
      return <UuidListField field={field} value={value} setValue={setValue} />;
    case "markdown":
      return (
        <FieldShell label={field.label}>
          <HighlightedCodeEditor
            aria-label={field.label}
            language={configEditorLanguage(field.key, value)}
            value={draft}
            onChange={(event) => setDraft(field.key, event.target.value)}
            rows={5}
            minHeight={132}
            invalid={!!error}
          />
          {error && <p className="mt-1 text-xs text-[var(--destructive)]">{error}</p>}
        </FieldShell>
      );
  }
}

function ReferenceField({
  field,
  nodeType,
  value,
  setValue,
}: Pick<DomainFieldProps, "field" | "nodeType" | "value" | "setValue">) {
  const config = getReferenceFieldConfig(field.key, nodeType);
  if (!config) return null;
  const values = config.mode === "multi" ? toStringArray(value) : toSingleArray(value);

  return (
    <FieldShell label={field.label}>
      <ReferencePicker
        kind={config.kind}
        action={config.action}
        mode={config.mode}
        nodeType={nodeType}
        fieldKey={config.fieldKey}
        value={values}
        onChange={(ids) => setValue(field.key, config.mode === "multi" ? ids : ids[0] ?? "")}
      />
    </FieldShell>
  );
}

function toStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];
}

function toSingleArray(value: unknown): string[] {
  return typeof value === "string" && value ? [value] : [];
}
